// Pure helpers for the cleanup research pane's "Household & members"
// card. Baker's feedback: when /cleanup flags two contacts as a
// possible duplicate he needs to tell a TRUE duplicate (one person,
// two records → merge) from two DIFFERENT people who merely share a
// phone or last name (e.g. spouses → dismiss). In AC he consulted the
// "INSURANCE: Household Members 18+" notes (name | DOB | DL#). That
// field was never migrated; the OS-native equivalent is the
// first-class household member roster the contact-detail endpoint
// already returns. These helpers turn that roster into the
// name · DOB · age · role lines the card renders.
//
// Kept as a standalone pure module (not inlined in ResearchPane.tsx)
// so it is unit-tested in isolation and so the ResearchPane diff
// stays minimal.

import { normalizeDisplayName } from "../../../shared/lib/normalize-display-name";

// Minimal structural shape this module needs. Deliberately NOT
// imported from web/src/api.ts: the unit test lives under tests/** and
// the root (Cloudflare-Workers, no-DOM) tsconfig type-checks tests/**,
// so importing web/src/api.ts here would drag the DOM-typed api client
// into the Workers program and fail typecheck. The real
// `ContactHouseholdMember` (from web/src/api) is a structural superset
// of this, so ResearchPane passes those objects in unchanged.
export interface RosterMember {
  id: string;
  legal_first_name: string | null;
  legal_last_name: string | null;
  preferred_name: string | null;
  household_role: string | null;
  birthday: string | null;
}

// Grant's "no AC all-caps in the UI" rule — same idempotent shared
// normalizer cleanup-records.ts uses (inlined here, rather than
// importing displayLabel from cleanup-records, because that module
// type-imports web/src/api and would re-introduce the Workers-program
// problem described above).
function displayLabel(label: string): string {
  return normalizeDisplayName(label) || label;
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Parse a stored birthday. Birthdays are stored YYYY-MM-DD; be lenient
// (accept a leading date prefix, ignore any time component) and return
// null for anything that doesn't look like a real calendar date so the
// UI shows "no DOB on file" rather than a garbled string.
function parseBirthday(
  b: string | null | undefined,
): { y: number; mo: number; d: number } | null {
  if (!b) return null;
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(b.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  if (y < 1900 || y > 2100) return null;
  return { y, mo, d };
}

// Age in whole years as of `now` (defaults to today). Returns null when
// the birthday is missing/unparseable or implausible (negative / >130).
export function ageFromBirthday(
  b: string | null | undefined,
  now: Date = new Date(),
): number | null {
  const p = parseBirthday(b);
  if (!p) return null;
  let age = now.getFullYear() - p.y;
  const nowMo = now.getMonth() + 1;
  const nowD = now.getDate();
  if (nowMo < p.mo || (nowMo === p.mo && nowD < p.d)) age--;
  return age >= 0 && age <= 130 ? age : null;
}

// "Mar 3, 1980 · age 46" — or "no DOB on file" when absent. The age
// suffix is the strongest same-person-vs-spouse signal so it is always
// appended when computable.
export function fmtBirthday(
  b: string | null | undefined,
  now: Date = new Date(),
): string {
  const p = parseBirthday(b);
  if (!p) return "no DOB on file";
  const label = `${MONTHS[p.mo - 1] ?? String(p.mo)} ${p.d}, ${p.y}`;
  const age = ageFromBirthday(b, now);
  return age == null ? label : `${label} · age ${age}`;
}

// Display name for a roster line: prefer the preferred name, else
// legal first+last, run through the all-caps→display normalizer
// (Grant's "no AC all-caps in the UI" rule).
export function memberName(m: RosterMember): string {
  const preferred = (m.preferred_name ?? "").trim();
  const legal = [m.legal_first_name, m.legal_last_name]
    .filter((x): x is string => Boolean(x && x.trim()))
    .join(" ")
    .trim();
  const nm = preferred || legal;
  return nm ? displayLabel(nm) : "(unnamed contact)";
}

// "Primary" / "Spouse" / "Child" / "Member" — household_role title-cased,
// with a sensible fallback when the role is unset.
export function roleLabel(role: string | null | undefined): string {
  const r = (role ?? "").trim();
  if (!r) return "Member";
  return r.charAt(0).toUpperCase() + r.slice(1).toLowerCase();
}

// A neutral, factual research line (NOT a recommendation — it must not
// compete with the detector's probable-case verdict per UX rule #2).
// States what the household + DOB evidence shows so a steward can
// decide same-person vs different-people themselves.
//
//  - same household  → these may be two distinct members (spouses);
//                       merging collapses them into one person
//  - DOBs differ      → strong signal they are two different people
//  - DOBs match       → supports a same-person duplicate
//  - otherwise        → no extra signal (return null; render nothing)
export function sameHouseholdHint(args: {
  householdAId: string | null;
  householdALabel: string | null;
  householdBId: string | null;
  birthdayA: string | null;
  birthdayB: string | null;
}): { tone: "warn" | "info"; text: string } | null {
  const {
    householdAId,
    householdALabel,
    householdBId,
    birthdayA,
    birthdayB,
  } = args;

  const sameHh =
    householdAId != null &&
    householdBId != null &&
    householdAId === householdBId;

  const a = (birthdayA ?? "").trim();
  const b = (birthdayB ?? "").trim();
  const bothDob = a !== "" && b !== "";
  // Compare on the date prefix only (ignore any stored time component).
  const dobKey = (s: string) => s.slice(0, 10);
  const dobMatch = bothDob && dobKey(a) === dobKey(b);
  const dobDiffer = bothDob && dobKey(a) !== dobKey(b);

  if (sameHh) {
    const where = householdALabel
      ? ` (${displayLabel(householdALabel)})`
      : "";
    const dobBit = dobDiffer
      ? " Their birthdays also differ — strong signal these are two different people."
      : dobMatch
        ? " Their birthdays match, though — double-check this isn't one person entered twice."
        : "";
    return {
      tone: "warn",
      text:
        `Both records are already in the same household${where}. ` +
        `They may be two different members (e.g. spouses), not one ` +
        `duplicate — merging would collapse two people into one.` +
        dobBit,
    };
  }

  if (dobDiffer) {
    return {
      tone: "warn",
      text:
        `Birthdays differ between these two records — a strong signal ` +
        `they are two different people, not one duplicate. Confirm ` +
        `before merging.`,
    };
  }

  if (dobMatch) {
    return {
      tone: "info",
      text:
        `Birthdays match on both records — consistent with the same ` +
        `person entered twice.`,
    };
  }

  return null;
}
